import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { getUser } from '@frontend/auth-helpers';
import { parseColumnId } from '../../components/calendar-view/calendar-view-v3/helper';
import { CalendarViewOffices } from '../../components/calendar-view/types';
import { useCalendarLocationIds } from '../../stores/calendar-location.store';
import { useCalendarStore } from '../../stores/calendar.store';
import { useSchedulingLocationInfo } from '../use-scheduling-location-info';
import { findEventById, formatEventDateTimeForTracking, getLocationTimezone } from './use-appointment-drag-drop';

type AppointmentDragTrackingData = {
  appointmentId: string;
  appointmentType: string;
  clientId?: string;
  providerId?: string;
  prevProviderId?: string;
  newProviderId?: string;
  operatoryId?: string;
  prevOperatoryId?: string;
  newOperatoryId?: string;
  startDateTime?: string;
  endDateTime?: string;
  prevStartDateTime?: string;
  prevEndDateTime?: string;
  newStartDateTime?: string;
  newEndDateTime?: string;
};

export const useAppointmentDragDropPendoTracking = (data: CalendarViewOffices | undefined) => {
  const userId = getUser()?.userID || '';
  const { selectedOrgId } = useSchedulingLocationInfo();
  const { calendarLocationIds } = useCalendarLocationIds();
  const { calendarEntityType } = useCalendarStore(['calendarEntityType']);

  const trackPendoEvent = (
    eventName: SchedulerV3.Types.PendoTrackingEventName,
    appointmentData?: AppointmentDragTrackingData
  ) => {
    SchedulerV3.trackPendoEvent(eventName, {
      userId,
      organizationId: selectedOrgId,
      locationIds: calendarLocationIds,
      scheduleView: calendarEntityType,
      ...appointmentData,
    });
  };

  const trackEvent = (
    eventName: SchedulerV3.Types.PendoTrackingEventName,
    options?: {
      eventId: string;
      sourceColumnId?: string;
      sourceColumnEntityId?: string;
      targetColumnEntityId?: string;
      newStartDateTime?: string;
      newEndDateTime?: string;
    }
  ) => {
    const {
      eventId = '',
      sourceColumnId,
      sourceColumnEntityId,
      targetColumnEntityId,
      newStartDateTime,
      newEndDateTime,
    } = options || {};

    const originalEvent = findEventById(data, eventId);
    const locationTimezone = getLocationTimezone(data, originalEvent?.locationId);
    const prevStartDateTime = formatEventDateTimeForTracking(
      originalEvent?.startDate,
      originalEvent?.startHour,
      locationTimezone
    );
    const prevEndDateTime = formatEventDateTimeForTracking(
      originalEvent?.endDate,
      originalEvent?.endHour,
      locationTimezone
    );

    if (eventName === 'appointment_card_dragged') {
      const { entityId } = parseColumnId(sourceColumnId);
      trackPendoEvent(eventName, {
        appointmentId: originalEvent?.eventId || '',
        appointmentType: originalEvent?.type || '',
        clientId: originalEvent?.patientId,
        providerId: calendarEntityType === 'provider' ? entityId : '',
        operatoryId: calendarEntityType === 'operatory' ? entityId : '',
        startDateTime: prevStartDateTime,
        endDateTime: prevEndDateTime,
      });
    } else if (
      eventName === 'appointment_card_drag_confirm_button_clicked' ||
      eventName === 'appointment_card_drag_cancel_button_clicked'
    ) {
      trackPendoEvent(eventName, {
        appointmentId: originalEvent?.eventId || '',
        appointmentType: originalEvent?.type || '',
        clientId: originalEvent?.patientId,
        prevProviderId: calendarEntityType === 'provider' ? sourceColumnEntityId : '',
        newProviderId: calendarEntityType === 'provider' ? targetColumnEntityId : '',
        prevOperatoryId: calendarEntityType === 'operatory' ? sourceColumnEntityId : '',
        newOperatoryId: calendarEntityType === 'operatory' ? targetColumnEntityId : '',
        prevStartDateTime,
        prevEndDateTime,
        newStartDateTime,
        newEndDateTime,
      });
    }
  };

  return trackEvent;
};
